import { FC, useMemo, useState } from 'react';

import { Button, type IconProps, TabList } from '../../../shared/ui';
import { useAppSelector } from '../../../hooks/redux';
import { declOfNum } from '../../../utils/decOfNum';
import { Love } from '../../icons/reactions/love';
import { Joy } from '../../icons/reactions/joy';
import { Sadness } from '../../icons/reactions/sadness';
import { Fear } from '../../icons/reactions/fear';
import { Anger } from '../../icons/reactions/anger';

import { EmoView } from './emo-view';

export interface EmotionsGroupClientProps {
  sendEmotionsSelect: (result: { category: string[]; emo: string[] }) => void;
}

type ViewMode = 'own' | 'shared';

const EMOTION_ORDER = ['Любовь', 'Радость', 'Грусть', 'Страх', 'Гнев'] as const;

const emotionIcons: Record<string, FC<IconProps>> = {
  Любовь: Love,
  Радость: Joy,
  Грусть: Sadness,
  Страх: Fear,
  Гнев: Anger,
};

/**
 * Клиентский вид Детектора эмоций в групповой терапии.
 * Выбор категорий и эмоций через EmoView, отправка по data channel по кнопке «Выбрать».
 * После выбора — переключатель «Мои эмоции / Эмоции на общем экране» (когда психолог открыл чей-то таб).
 */
export const EmotionsGroupClient: FC<EmotionsGroupClientProps> = ({ sendEmotionsSelect }) => {
  const psyTools = useAppSelector((state) => state.psyToolsSlice.tools);
  const sharedView = useAppSelector((state) => state.emotionsGroupSlice.sharedView);
  const [reactionId, setReactionId] = useState<string[]>([]);
  const [selectedEmo, setSelectedEmo] = useState<string[]>([]);
  const [selectionFinished, setSelectionFinished] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('own');

  const settings = psyTools.find((el) => el.name === 'emotions_detection')?.psy_tool_settings;

  const handleSelect = () => {
    if (selectedEmo.length === 0) return;
    const category = (settings ?? []).filter((s) => s.value.some((e) => selectedEmo.includes(e))).map((s) => s.name);
    sendEmotionsSelect({ category, emo: selectedEmo });
    setSelectionFinished(true);
  };

  const onEmotionClick = (item: string, selected: string[]) => {
    if (selected.includes(item)) {
      setSelectedEmo(selected.filter((el) => el !== item));
    } else {
      setSelectedEmo([...selected, item]);
    }
  };

  /** Группы эмоций по категориям (как у психолога): слева смайлик + группа, справа теги */
  const buildEmotionsByGroup = (categories: string[], emoList: string[]) => {
    if (!settings?.length || !emoList.length) return [];
    const emoSet = new Set(emoList);
    const result: { category: string; items: string[] }[] = [];
    for (const cat of EMOTION_ORDER) {
      if (!categories.includes(cat)) continue;
      const items = (settings.find((s) => s.name === cat)?.value ?? []).filter((e) => emoSet.has(e));
      if (items.length > 0) result.push({ category: cat, items });
    }
    return result;
  };

  const ownEmotionsByGroup = useMemo(() => {
    const category = (settings ?? []).filter((s) => s.value.some((e) => selectedEmo.includes(e))).map((s) => s.name);
    return buildEmotionsByGroup(category, selectedEmo);
  }, [selectedEmo, settings]);

  const sharedEmotionsByGroup = useMemo(() => {
    if (!sharedView?.emo?.length || !settings?.length) return [];
    const emoSet = new Set(sharedView.emo);
    const result: { category: string; items: string[] }[] = [];
    for (const cat of EMOTION_ORDER) {
      const items = (settings.find((s) => s.name === cat)?.value ?? []).filter((e) => emoSet.has(e));
      if (items.length > 0) result.push({ category: cat, items });
    }
    return result;
  }, [sharedView?.emo, settings]);

  if (selectionFinished) {
    const hasShared = Boolean(sharedView && sharedView.emo.length > 0);
    const showToggle = hasShared;
    const isOwn = viewMode === 'own';
    const emotionsByGroup = isOwn ? ownEmotionsByGroup : sharedEmotionsByGroup;
    const flatEmo = isOwn ? selectedEmo : (sharedView?.emo ?? []);
    const sharedLabel = sharedView?.participantLabel
      ? `Эмоции участника ${sharedView.participantLabel}`
      : 'Эмоции на общем экране';

    return (
      <div className="w-full h-full min-h-0 pr-[0.25rem] flex flex-col">
        <div className="w-full h-full min-h-0 flex flex-col rounded-chip md:rounded-card overflow-hidden">
          {showToggle && (
            <div className="flex shrink-0 gap-2 flex-wrap p-3.5 md:p-4 pb-2">
              <TabList<ViewMode>
                variant="segmented"
                value={viewMode}
                onChange={setViewMode}
                items={[
                  { id: 'own', label: 'Мои эмоции' },
                  { id: 'shared', label: sharedLabel },
                ]}
              />
            </div>
          )}
          <div className="flex-1 min-h-0 overflow-y-auto px-3.5 md:px-4 pb-4 white-scroll w-full">
            {emotionsByGroup.length > 0 ? (
              <div className="flex flex-col gap-4 w-full">
                {emotionsByGroup.map((group) => {
                  const IconComponent = emotionIcons[group.category];
                  const count = group.items.length;

                  return (
                    <div
                      key={group.category}
                      className="flex flex-col sm:flex-row gap-3 sm:gap-4 p-4 rounded-control bg-white shadow-raised border border-line min-w-0"
                    >
                      <div className="flex items-center gap-3 shrink-0">
                        <div className="w-[80px] h-[80px] min-w-[80px] min-h-[80px] bg-surface-muted rounded-control flex items-center justify-center">
                          {IconComponent ? (
                            <IconComponent
                              needHover={false}
                              stroke="#63656A"
                              className="w-[50px] h-[50px] !cursor-default"
                            />
                          ) : null}
                        </div>
                        <div className="flex flex-col">
                          <p className="text-ink text-heading-md">{group.category}</p>
                          <p className="text-ink-muted text-ui">
                            {count} {declOfNum(count, ['эмоция', 'эмоции', 'эмоций'])}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 items-center min-w-0 flex-1">
                        {group.items.map((item) => (
                          <span key={item} className="bg-accent text-ink px-3 py-1.5 rounded-chip text-ui-strong">
                            {item}
                          </span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2 w-full">
                {flatEmo.map((el, idx) => (
                  <span key={idx} className="bg-accent text-ink px-3 py-1.5 rounded-chip text-ui-strong">
                    {el}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-full min-h-0 pr-[0.25rem] flex flex-col">
      <div className="w-full h-full min-h-0 flex flex-col rounded-chip md:rounded-card overflow-hidden">
        <div className="flex-1 min-h-0 overflow-y-auto p-4 pb-24 white-scroll">
          <EmoView
            isGroup
            settings={settings}
            selectedEmo={selectedEmo}
            onEmotionClick={onEmotionClick}
            reactionId={reactionId}
            setReactionId={setReactionId}
          />
        </div>
        <div className="flex-shrink-0 w-full flex justify-center py-3 xl:pb-4">
          <Button
            size="md"
            variant="primary"
            className="w-[200px]"
            disabled={selectedEmo.length === 0}
            onClick={handleSelect}
          >
            Выбрать
          </Button>
        </div>
      </div>
    </div>
  );
};
